import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { apiStore } from '@/stores/ApiStore';
import { authFlowStore } from '@/stores/AuthFlowStore';
import { AccountLayout } from '@/components/ui/AccountLayout';
import { PaywallHeader } from '@/components/paywall/PaywallHeader';
import { CtaButton } from '@/components/ui/CtaButton';
import { ROUTES } from '@/constants/routes';

const Header = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  height: 100%;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  color: #000000;
`;

const Input = styled.input<{ $error?: boolean }>`
  width: 100%;
  padding: 18px 16px;
  border: 1.5px solid ${({ $error }) => ($error ? 'var(--error-color)' : '#e5e7eb')};
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.25;
  outline: none;
  transition: border-color 0.3s ease;

  &::placeholder {
    color: rgba(0, 0, 0, 0.5);
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? 'var(--error-color)' : '#000000')};
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const CtaSection = styled.div`
  margin-top: auto;
  margin-bottom: 24px;
`;

export const LoginPage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailRef = useRef<(HTMLInputElement | null)>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const isRelogin = searchParams.get('relogin') === 'true';

  useEffect(() => {
    emailRef.current?.focus();

    // Поддержка email параметра из URL для WebView интеграции
    const emailFromUrl = searchParams.get('email');

    if (emailFromUrl && typeof emailFromUrl === 'string') {
      // Email приходит зашифрованным - нужно дешифровать
      void (async () => {
        try {
          const decodeResult = await apiStore.decodeLink(emailFromUrl);
          if (decodeResult.ok && decodeResult.data) {
            authFlowStore.setEmail(decodeResult.data);
          }
          else {
            setSubmitError('Invalid email code');
          }
        }
        catch {
          setSubmitError('Failed to decode email');
        }
      })();
    }
  }, [searchParams]);

  const validateEmail = (email: string): string | null => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      return 'Email is invalid';
    }
    return null;
  };

  const handleEmailChange = (value: string) => {
    authFlowStore.setEmail(value);
    setSubmitError(null);
    if (touched) {
      setValidationError(validateEmail(value));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setValidationError(validateEmail(authFlowStore.email));
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;

    setTouched(true);
    const email = authFlowStore.email.trim();
    const error = validateEmail(email);
    setValidationError(error);

    if (!email || error) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      authFlowStore.setEmail(email);
      const result = await apiStore.requestCode(email);
      if (result.ok) {
        // Пробрасываем параметры relogin, restore и meta на страницу кода
        const params = new URLSearchParams();
        if (isRelogin) {
          params.set('relogin', 'true');
        }
        const restoreParam = searchParams.get('restore');
        if (restoreParam) {
          params.set('restore', restoreParam);
        }
        const metaParam = searchParams.get('meta');
        if (metaParam) {
          params.set('meta', metaParam);
        }
        const codeUrl = params.toString()
          ? `${ROUTES.AUTH.LOGIN_CODE}?${params.toString()}`
          : ROUTES.AUTH.LOGIN_CODE;
        navigate(codeUrl);
      }
      else {
        setSubmitError('Something went wrong. Try again later');
      }
    }
    catch {
      setSubmitError('Something went wrong. Try again later');
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const hasError = validationError || submitError;
  const errorMessage = validationError || submitError;
  const normalizedEmail = authFlowStore.email.trim();
  const emailFormatError = validateEmail(normalizedEmail);
  const showCta = !!normalizedEmail && !emailFormatError && !validationError && !submitError;

  return (
    <AccountLayout showLogo>
      <Header>
        <PaywallHeader>Enter with email</PaywallHeader>
      </Header>

      <Form onSubmit={submit}>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          ref={emailRef}
          value={authFlowStore.email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={handleBlur}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="example@gmail.com"
          $error={touched && !!hasError}
          onKeyUp={(e) => e.key === 'Enter' && void submit(e)}
        />

        {hasError && <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>}

        {showCta && (
          <CtaSection>
            <CtaButton label="Continue" loading={isSubmitting} onClick={submit} />
          </CtaSection>
        )}
      </Form>
    </AccountLayout>
  );
});
